import * as XLSX from 'xlsx';
import { Transaction } from '../types';

export const exportTransactionsToExcel = (transactions: Transaction[], monthLabel: string) => {
  const wb = XLSX.utils.book_new();

  // 1. Summary Sheet
  const summary = transactions.reduce((acc, curr) => {
    const val = Number(curr.amount);
    if (curr.type === 'income') acc.income += val;
    else if (curr.type === 'expense') acc.expense += val;
    else if (curr.type === 'debt') acc.debt += val;
    else if (curr.type === 'receivable') acc.receivable += val;
    return acc;
  }, { income: 0, expense: 0, debt: 0, receivable: 0 });

  const summaryData = [
    { 'Metric': 'Total Income', 'Amount (INR)': summary.income },
    { 'Metric': 'Total Expenses', 'Amount (INR)': summary.expense },
    { 'Metric': 'Total Borrowed (Debt)', 'Amount (INR)': summary.debt },
    { 'Metric': 'Total Lent (Credit)', 'Amount (INR)': summary.receivable },
    { 'Metric': 'Net Cash Flow', 'Amount (INR)': summary.income - summary.expense }
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Financial Summary');

  // 2. Transaction Sheets
  const filterAndFormat = (type: string) => {
    return transactions
      .filter(t => t.type === type)
      .map(t => ({
        Date: t.date,
        Category: t.category,
        Amount: t.amount,
        Note: t.note || ''
      }));
  };

  const incomeData = filterAndFormat('income');
  const expenseData = filterAndFormat('expense');
  const debtData = filterAndFormat('debt');
  const receivableData = filterAndFormat('receivable');

  if (incomeData.length > 0) {
    const ws = XLSX.utils.json_to_sheet(incomeData);
    XLSX.utils.book_append_sheet(wb, ws, 'Income Details');
  }

  if (expenseData.length > 0) {
    const ws = XLSX.utils.json_to_sheet(expenseData);
    XLSX.utils.book_append_sheet(wb, ws, 'Expense Details');
  }

  if (debtData.length > 0 || receivableData.length > 0) {
    const debtRecData = [
      ...debtData.map(d => ({ ...d, Type: 'Debt (Borrowed)' })),
      ...receivableData.map(r => ({ ...r, Type: 'Credit (Lent)' }))
    ];
    const ws = XLSX.utils.json_to_sheet(debtRecData);
    XLSX.utils.book_append_sheet(wb, ws, 'Debts & Receivables');
  }

  XLSX.writeFile(wb, `SecureTrack_Report_${monthLabel.replace(/\s/g, '_')}.xlsx`);
};

export const exportGroupToExcel = (
  groupName: string,
  totalSpent: number,
  memberCount: number,
  contributions: Record<string, number>,
  userMap: Record<string, string>,
  settlements: any[],
  transactions: Transaction[]
) => {
  const wb = XLSX.utils.book_new();
  const fairShare = totalSpent / (memberCount || 1);

  const summaryData = Object.entries(userMap).map(([id, name]) => {
    const paid = contributions[id] || 0;
    const balance = paid - fairShare;
    return {
      'Member Name': name,
      'Total Paid (INR)': paid,
      'Fair Share (INR)': fairShare,
      'Net Balance (INR)': balance,
      'Status': balance >= 0 ? 'To Collect' : 'Must Pay'
    };
  });
  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Group Summary');

  const settlementData = settlements.length > 0 
    ? settlements.map(s => ({
        'From': s.from,
        'Action': 'Pays',
        'To': s.to,
        'Amount (INR)': s.amount
      }))
    : [{ Message: 'Everyone is balanced!' }];
  const wsSettlements = XLSX.utils.json_to_sheet(settlementData);
  XLSX.utils.book_append_sheet(wb, wsSettlements, 'Settlement Plan');

  const historyData = transactions.map(t => ({
    'Date': t.date,
    'Member': userMap[t.user_id] || 'Unknown',
    'Category': t.category,
    'Type': t.type,
    'Amount (INR)': t.amount,
    'Note': t.note || ''
  }));
  const wsHistory = XLSX.utils.json_to_sheet(historyData);
  XLSX.utils.book_append_sheet(wb, wsHistory, 'Full History');

  XLSX.writeFile(wb, `${groupName.replace(/\s/g, '_')}_Group_Report.xlsx`);
};